import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import useUrlStateObject from "@/hooks/useUrlStateObject";
import DashboardSidebar from "@/pages/dashboard/components/DashboardSidebar";
import MobileBottomNav from "@/pages/dashboard/components/MobileBottomNav";
import ScheduleTable from "./components/ScheduleTable";
import ScheduleDetailModal from "./components/ScheduleDetailModal";
import EditTimeModal from "./components/EditTimeModal";
import { ScheduleItem, ScheduleStatus, TYPE_CONFIG, mapDbToScheduleItem } from "./types";
import { useScheduledSendPoller } from "@/hooks/useScheduledSendPoller";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

type FilterStatus = "all" | ScheduleStatus;
type SortKey = "scheduledAt" | "createdAt" | "recipients" | "cost";
type SortDir = "asc" | "desc";

// 예약 발송 필터를 주소(URL)에 남겨 새로고침·링크 공유에도 유지 (sessionStorage 백업)
interface ScheduleUrlState extends Record<string, string> {
  status: string;
  types: string;
  q: string;
  sort: string;
  dir: string;
}

const SCHEDULE_URL_DEFAULTS: ScheduleUrlState = {
  status: "all",
  types: "",
  q: "",
  sort: "scheduledAt",
  dir: "asc",
};

// 탭 복귀(리로드) 시 로딩 없이 마지막 예약 목록을 즉시 보여주기 위한 캐시
const SCHEDULE_CACHE_KEY = "cache:user_schedules";

function readScheduleCache(): ScheduleItem[] | null {
  try {
    const raw = sessionStorage.getItem(SCHEDULE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ScheduleItem[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeScheduleCache(list: ScheduleItem[]): void {
  try {
    sessionStorage.setItem(SCHEDULE_CACHE_KEY, JSON.stringify(list));
  } catch {
    // 캐시 저장 실패(용량 초과 등)는 무시
  }
}

export default function SchedulePage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, session } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const cachedSchedulesRef = useRef<ScheduleItem[] | null>(readScheduleCache());
  const [schedules, setSchedules] = useState<ScheduleItem[]>(cachedSchedulesRef.current ?? []);
  const [loading, setLoading] = useState(cachedSchedulesRef.current === null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // 캐시도 함께 갱신하는 setSchedules 래퍼
  const updateSchedules = useCallback((updater: (prev: ScheduleItem[]) => ScheduleItem[]) => {
    setSchedules((prev) => {
      const next = updater(prev);
      writeScheduleCache(next);
      return next;
    });
  }, []);

  // Filters — 주소(URL)에 남겨 새로고침·링크 공유에도 유지 (sessionStorage 백업)
  const [urlState, setUrlState] = useUrlStateObject<ScheduleUrlState>("schedule_filter", SCHEDULE_URL_DEFAULTS);

  const statusFilter = (urlState.status || "all") as FilterStatus;
  const typeFilter = useMemo(
    () => (urlState.types ? urlState.types.split(",").filter(Boolean) : []),
    [urlState.types]
  );
  const searchQuery = urlState.q;
  const sortKey = (urlState.sort || "scheduledAt") as SortKey;
  const sortDir: SortDir = urlState.dir === "desc" ? "desc" : "asc";

  const setStatusFilter = useCallback(
    (next: FilterStatus) => setUrlState({ ...urlState, status: next }),
    [urlState, setUrlState]
  );
  const setSearchQuery = useCallback(
    (next: string) => setUrlState({ ...urlState, q: next }),
    [urlState, setUrlState]
  );
  const setTypeFilter = useCallback(
    (next: string[]) => setUrlState({ ...urlState, types: next.join(",") }),
    [urlState, setUrlState]
  );
  const setSort = useCallback(
    (key: SortKey, dir: SortDir) => setUrlState({ ...urlState, sort: key, dir }),
    [urlState, setUrlState]
  );

  // Modals
  const [detailItem, setDetailItem] = useState<ScheduleItem | null>(null);
  const [editItem, setEditItem] = useState<ScheduleItem | null>(null);
  const [restoreItem, setRestoreItem] = useState<ScheduleItem | null>(null);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Poll for scheduled sends that are due
  const { triggerNow: triggerPoll } = useScheduledSendPoller({
    onProcessed: (count: number, results: Array<{ historyId: string; status: string }>) => {
      const successCount = results.filter((r) => r.status === "success").length;
      const failCount = results.filter((r) => r.status !== "success").length;
      const msg = successCount > 0
        ? `${count}건 예약 발송 처리 완료 (성공 ${successCount}건${failCount > 0 ? `, 실패 ${failCount}건` : ""})`
        : `${count}건 예약 발송 처리됨`;
      showToast(msg, failCount > 0 && successCount === 0 ? "error" : "success");
      fetchSchedules(); // auto-refresh
    },
    onError: (message: string) => {
      console.warn("[SchedulePage] Poller error:", message);
    },
    onRefreshNeeded: () => {
      fetchSchedules(); // auto-refresh on any processing
    },
  });

  // Auth guard
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  // Fetch schedules
  const fetchSchedules = useCallback(async () => {
    if (!user || !session) return;
    // 캐시된 목록이 있으면 로딩 화면 없이 조용히 갱신
    if (cachedSchedulesRef.current === null) setLoading(true);
    setFetchError(null);
    try {
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/get-user-schedules`, {
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      const mapped = (data.schedules || []).map(mapDbToScheduleItem);
      setSchedules(mapped);
      writeScheduleCache(mapped);
      cachedSchedulesRef.current = mapped;
    } catch (err: any) {
      // 캐시가 있으면 화면의 값을 유지하고 조용히 실패
      if (cachedSchedulesRef.current === null) {
        setFetchError(err.message || "데이터를 불러오는 중 오류가 발생했습니다.");
      }
    } finally {
      cachedSchedulesRef.current = cachedSchedulesRef.current ?? [];
      setLoading(false);
    }
  }, [user, session]);

  useEffect(() => {
    if (user && session) {
      fetchSchedules();
    }
  }, [user, session, fetchSchedules]);

  // Stats
  const stats = useMemo(() => {
    const active = schedules.filter((s) => s.status === "scheduled");
    const cancelled = schedules.filter((s) => s.status === "cancelled");
    const totalRecipients = active.reduce((sum, s) => sum + s.recipients.length, 0);
    const totalCost = active.reduce((sum, s) => sum + s.cost, 0);
    return { active: active.length, cancelled: cancelled.length, totalRecipients, totalCost };
  }, [schedules]);

  // Filtered & sorted
  const filtered = useMemo(() => {
    let list = [...schedules];
    if (statusFilter !== "all") list = list.filter((s) => s.status === statusFilter);
    if (typeFilter.length > 0) list = list.filter((s) => typeFilter.includes(s.type));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (s) =>
          s.message.toLowerCase().includes(q) ||
          (s.subject ?? "").toLowerCase().includes(q) ||
          s.recipients.some((r) => r.phone.includes(q) || (r.name ?? "").toLowerCase().includes(q))
      );
    }
    list.sort((a, b) => {
      let va: number | string = 0;
      let vb: number | string = 0;
      if (sortKey === "scheduledAt") { va = a.scheduledAt; vb = b.scheduledAt; }
      else if (sortKey === "createdAt") { va = a.createdAt; vb = b.createdAt; }
      else if (sortKey === "recipients") { va = a.recipients.length; vb = b.recipients.length; }
      else if (sortKey === "cost") { va = a.cost; vb = b.cost; }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [schedules, statusFilter, typeFilter, searchQuery, sortKey, sortDir]);

  const handleCancel = async (item: ScheduleItem) => {
    if (!session) return;
    try {
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/cancel-scheduled-send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ history_id: item.id }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      updateSchedules((prev) =>
        prev.map((s) => (s.id === item.id ? { ...s, status: "cancelled" as ScheduleStatus } : s))
      );
      setDetailItem(null);
      showToast("예약이 취소되었습니다.");
    } catch (err: any) {
      showToast(err.message || "취소 중 오류가 발생했습니다.", "error");
    }
  };

  const handleEditTime = async (item: ScheduleItem, newTime: string) => {
    if (!session) return;
    const parts = newTime.match(/(\d{4})\.(\d{2})\.(\d{2})\s(\d{2}):(\d{2})/);
    if (!parts) {
      showToast("올바른 시간 형식이 아닙니다.", "error");
      return;
    }
    const isoStr = `${parts[1]}-${parts[2]}-${parts[3]}T${parts[4]}:${parts[5]}:00+09:00`;

    try {
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/update-schedule-time`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ history_id: item.id, scheduled_at: isoStr }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      updateSchedules((prev) =>
        prev.map((s) => (s.id === item.id ? { ...s, scheduledAt: newTime } : s))
      );
      setEditItem(null);
      showToast(`발송 시간이 ${newTime}으로 변경되었습니다.`);
    } catch (err: any) {
      showToast(err.message || "시간 변경 중 오류가 발생했습니다.", "error");
    }
  };

  const handleRestore = (item: ScheduleItem) => {
    setRestoreItem(item);
  };

  const handleRestoreConfirm = async (newTime: string) => {
    if (!restoreItem || !session) return;

    const parts = newTime.match(/(\d{4})\.(\d{2})\.(\d{2})\s(\d{2}):(\d{2})/);
    if (!parts) {
      showToast("올바른 시간 형식이 아닙니다.", "error");
      return;
    }
    const isoStr = `${parts[1]}-${parts[2]}-${parts[3]}T${parts[4]}:${parts[5]}:00+09:00`;

    try {
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/update-schedule-time`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ history_id: restoreItem.id, scheduled_at: isoStr }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      updateSchedules((prev) =>
        prev.map((s) =>
          s.id === restoreItem.id
            ? { ...s, status: "scheduled" as ScheduleStatus, scheduledAt: newTime }
            : s
        )
      );
      setRestoreItem(null);
      showToast("예약이 재설정되었습니다.");
    } catch (err: any) {
      showToast(err.message || "재예약 중 오류가 발생했습니다.", "error");
    }
  };

  const toggleTypeFilter = (type: string) => {
    setTypeFilter(
      typeFilter.includes(type) ? typeFilter.filter((t) => t !== type) : [...typeFilter, type]
    );
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSort(key, sortDir === "asc" ? "desc" : "asc");
    } else {
      setSort(key, "asc");
    }
  };

  const statusTabs: { key: FilterStatus; label: string; count: number }[] = [
    { key: "all", label: "전체", count: schedules.length },
    { key: "scheduled", label: "예약 대기", count: schedules.filter((s) => s.status === "scheduled").length },
    { key: "cancelled", label: "취소됨", count: schedules.filter((s) => s.status === "cancelled").length },
  ];

  const sidebarWidth = collapsed ? "md:ml-16" : "md:ml-60";

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a14] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-violet-400 text-3xl"></i>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0a0a14] flex">
      <DashboardSidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />

      <main className={`flex-1 transition-all duration-300 ${sidebarWidth} min-h-screen flex flex-col pb-20 md:pb-0`}>
        {/* Top bar */}
        <div className="h-14 md:h-16 flex items-center justify-between px-4 md:px-6 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
            <button
              onClick={() => navigate("/dashboard")}
              className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer transition-colors md:hidden"
            >
              <i className="ri-arrow-left-line text-base"></i>
            </button>
            <span className="text-gray-500 hidden md:inline">대시보드</span>
            <i className="ri-arrow-right-s-line text-gray-600 hidden md:inline"></i>
            <span className="text-white font-medium">예약 발송</span>
          </div>
          <Link
            to="/dashboard/send"
            className="flex items-center gap-1.5 px-3 py-1.5 md:px-4 md:py-2 rounded-lg bg-gradient-to-r from-violet-600 to-pink-600 text-white text-xs md:text-sm font-medium hover:from-violet-500 hover:to-pink-500 cursor-pointer transition-all whitespace-nowrap"
          >
            <i className="ri-add-line text-xs md:text-sm"></i>
            <span className="hidden sm:inline">새 예약 발송</span>
            <span className="sm:hidden">발송</span>
          </Link>
        </div>

        <div className="flex-1 p-4 md:p-6 space-y-4 md:space-y-5">
          {/* Page title - desktop only */}
          <div className="hidden md:block">
            <h1 className="text-white text-xl font-semibold">예약 발송</h1>
            <p className="text-gray-500 text-sm mt-0.5">예약된 문자 발송을 관리하고 시간을 수정하세요</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
            <div className="px-3 py-3 md:px-5 md:py-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-lg bg-violet-500/20">
                  <i className="ri-calendar-schedule-line text-violet-400 text-xs md:text-sm"></i>
                </div>
                <span className="text-gray-400 text-[10px] md:text-xs">예약 대기</span>
              </div>
              <p className="text-white text-lg md:text-2xl font-semibold">{stats.active}</p>
              <p className="text-gray-600 text-[10px] md:text-xs mt-0.5">건</p>
            </div>
            <div className="px-3 py-3 md:px-5 md:py-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-lg bg-emerald-500/20">
                  <i className="ri-group-line text-emerald-400 text-xs md:text-sm"></i>
                </div>
                <span className="text-gray-400 text-[10px] md:text-xs">총 수신자</span>
              </div>
              <p className="text-white text-lg md:text-2xl font-semibold">{stats.totalRecipients.toLocaleString()}</p>
              <p className="text-gray-600 text-[10px] md:text-xs mt-0.5">명</p>
            </div>
            <div className="px-3 py-3 md:px-5 md:py-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-lg bg-amber-500/20">
                  <i className="ri-coin-line text-amber-400 text-xs md:text-sm"></i>
                </div>
                <span className="text-gray-400 text-[10px] md:text-xs">예상 비용</span>
              </div>
              <p className="text-white text-lg md:text-2xl font-semibold">₩{stats.totalCost.toLocaleString()}</p>
              <p className="text-gray-600 text-[10px] md:text-xs mt-0.5">예약 대기 기준</p>
            </div>
            <div className="px-3 py-3 md:px-5 md:py-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-lg bg-gray-500/20">
                  <i className="ri-close-circle-line text-gray-400 text-xs md:text-sm"></i>
                </div>
                <span className="text-gray-400 text-[10px] md:text-xs">취소됨</span>
              </div>
              <p className="text-white text-lg md:text-2xl font-semibold">{stats.cancelled}</p>
              <p className="text-gray-600 text-[10px] md:text-xs mt-0.5">건</p>
            </div>
          </div>

          {/* Filter & Table */}
          <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
            {/* Filter bar */}
            <div className="px-3 md:px-5 py-3 md:py-4 border-b border-white/[0.06] space-y-2 md:space-y-3">
              {/* Status tabs + search */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                {/* Status tabs */}
                <div className="flex items-center gap-1 p-1 rounded-lg bg-white/[0.04] border border-white/[0.06] overflow-x-auto">
                  {statusTabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setStatusFilter(tab.key)}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                        statusFilter === tab.key
                          ? "bg-white/[0.1] text-white"
                          : "text-gray-500 hover:text-gray-300"
                      }`}
                    >
                      {tab.label}
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                        statusFilter === tab.key ? "bg-white/[0.15] text-white" : "bg-white/[0.06] text-gray-500"
                      }`}>
                        {tab.count}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Search + Refresh */}
                <div className="flex items-center gap-2 sm:ml-auto">
                  <div className="relative flex-1 sm:max-w-xs">
                    <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                      <i className="ri-search-line text-gray-500 text-xs"></i>
                    </div>
                    <input
                      type="text"
                      placeholder="메시지, 수신번호 검색..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg pl-8 pr-3 py-2 text-white text-xs placeholder-gray-600 focus:outline-none focus:border-violet-500/40"
                    />
                  </div>
                  <button
                    onClick={fetchSchedules}
                    disabled={loading}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] border border-white/[0.08] text-gray-400 hover:text-white cursor-pointer transition-colors shrink-0"
                    title="새로고침"
                  >
                    <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`}></i>
                  </button>
                </div>
              </div>

              {/* Type filter + Sort */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                {/* Type filter */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-gray-500 text-[10px] md:text-xs">유형:</span>
                  {(["SMS", "LMS", "MMS"] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => toggleTypeFilter(type)}
                      className={`px-2 py-1 rounded-md text-[10px] md:text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                        typeFilter.includes(type)
                          ? TYPE_CONFIG[type].className + " border border-current/30"
                          : "bg-white/[0.04] text-gray-500 hover:text-gray-300 border border-white/[0.06]"
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                  {typeFilter.length > 0 && (
                    <button
                      onClick={() => setTypeFilter([])}
                      className="text-gray-600 text-[10px] md:text-xs hover:text-gray-400 cursor-pointer transition-colors"
                    >
                      초기화
                    </button>
                  )}
                </div>

                {/* Sort */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-gray-500 text-[10px] md:text-xs">정렬:</span>
                  {(
                    [
                      { key: "scheduledAt" as SortKey, label: "예약 시각" },
                      { key: "createdAt" as SortKey, label: "등록일" },
                      { key: "recipients" as SortKey, label: "수신자" },
                      { key: "cost" as SortKey, label: "비용" },
                    ] as const
                  ).map((s) => (
                    <button
                      key={s.key}
                      onClick={() => toggleSort(s.key)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] md:text-xs cursor-pointer transition-all whitespace-nowrap ${
                        sortKey === s.key
                          ? "bg-violet-600/20 text-violet-300 border border-violet-500/20"
                          : "bg-white/[0.04] text-gray-500 hover:text-gray-300 border border-white/[0.06]"
                      }`}
                    >
                      {s.label}
                      {sortKey === s.key && (
                        <i className={`${sortDir === "asc" ? "ri-arrow-up-line" : "ri-arrow-down-line"} text-[10px]`}></i>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Table or Loading/Error */}
            {loading ? (
              <div className="py-16 md:py-20 flex flex-col items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-violet-400 text-xl md:text-2xl mb-3"></i>
                <p className="text-gray-500 text-xs md:text-sm">예약 발송 내역을 불러오는 중...</p>
              </div>
            ) : fetchError ? (
              <div className="py-16 md:py-20 flex flex-col items-center justify-center">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-2xl bg-red-500/10 mb-3">
                  <i className="ri-error-warning-line text-red-400 text-lg md:text-xl"></i>
                </div>
                <p className="text-gray-300 text-xs md:text-sm font-medium mb-1">데이터를 불러오지 못했습니다</p>
                <p className="text-gray-500 text-[10px] md:text-xs mb-4">{fetchError}</p>
                <button
                  onClick={fetchSchedules}
                  className="px-4 py-2 rounded-lg bg-violet-600/30 text-violet-300 text-xs md:text-sm hover:bg-violet-600/40 cursor-pointer transition-colors whitespace-nowrap"
                >
                  다시 시도
                </button>
              </div>
            ) : (
              <ScheduleTable
                items={filtered}
                onViewDetail={setDetailItem}
                onEditTime={setEditItem}
                onCancel={handleCancel}
                onRestore={handleRestore}
              />
            )}
          </div>
        </div>

        <MobileBottomNav />
      </main>

      {/* Detail Modal */}
      {detailItem && (
        <ScheduleDetailModal
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onEditTime={(item) => { setDetailItem(null); setEditItem(item); }}
          onCancel={handleCancel}
          onRestore={(item) => { setDetailItem(null); handleRestore(item); }}
        />
      )}

      {/* Edit Time Modal */}
      {editItem && (
        <EditTimeModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSave={handleEditTime}
        />
      )}

      {/* Restore (Re-schedule) Modal */}
      {restoreItem && (
        <EditTimeModal
          item={{ ...restoreItem, scheduledAt: "" }}
          onClose={() => setRestoreItem(null)}
          onSave={(_, newTime) => handleRestoreConfirm(newTime)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-20 md:bottom-6 right-4 md:right-6 flex items-center gap-2 md:gap-3 px-3 py-2.5 md:px-4 md:py-3 rounded-xl border shadow-lg z-[100] transition-all ${
            toast.type === "success"
              ? "bg-[#1a1a2e] border-emerald-500/30 text-emerald-400"
              : "bg-[#1a1a2e] border-red-500/30 text-red-400"
          }`}
        >
          <div className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center">
            <i className={`${toast.type === "success" ? "ri-checkbox-circle-line" : "ri-close-circle-line"} text-sm md:text-base`}></i>
          </div>
          <span className="text-xs md:text-sm">{toast.msg}</span>
        </div>
      )}
    </div>
  );
}